import React, {useEffect, useState} from 'react';
import {inject, observer} from 'mobx-react';
import {useNavigate, useParams} from 'react-router-dom';
import FeedbackListUI from '../view/FeedbackListUI';
import FeedbackStore from '../../store/FeedbackStore';
import NavigationStore from '../../../../navigation/store/NavigationStore';
import {Feedback} from '../../../../data/models/Feedback/Feedback';
import {APP_PERMISSIONS} from '../../../../data/permissions';
import ConfirmationModal from '../../../../uiToolkit/ConfirmationModal';
import ModalFade, {
  useModalFade,
} from '../../../Microreels/List/components/ModalFade';
import {feedbackText} from '../../feedbackText';

interface Props {
  feedbackStore?: FeedbackStore;
  navigationStore?: NavigationStore;
}

const FeedbackListContainer = inject(
  'feedbackStore',
  'navigationStore',
)(
  observer(({feedbackStore, navigationStore}: Props) => {
    const [feedbackList, setFeedbackList] = useState<Feedback[]>([]);
    const [page, setPage] = useState(0);
    const [pagesCount, setPagesCount] = useState(1);
    const [search, setSearch] = useState('');
    const [feedbackToDelete, setFeedbackToDelete] = useState<Feedback>();
    const {organizationId} = useParams();
    const navigate = useNavigate();

    const deleteFade = useModalFade(!!feedbackToDelete);
    const listRoute = `/manage/${organizationId}/feedback_questions`;
    const canEdit = (navigationStore?.userPermissions || []).includes(
      APP_PERMISSIONS.ORG_CREATE_COURSES,
    );

    const fetchFeedback = () => {
      if (!organizationId) return;
      setPage(0);
      feedbackStore!.getFeedbackList(organizationId, 0, search).then(res => {
        setFeedbackList(res.data);
        setPagesCount(res.pagesCount);
      });
    };

    useEffect(() => {
      fetchFeedback();
    }, [search, organizationId]);

    const loadNextPage = async () => {
      if (!organizationId || page + 1 >= pagesCount) return;
      const res = await feedbackStore!.getFeedbackList(
        organizationId,
        page + 1,
        search,
      );
      setFeedbackList(current => [...current, ...res.data]);
      setPage(page + 1);
    };

    const onConfirmDelete = async () => {
      if (!organizationId || !feedbackToDelete) return;
      const success = await feedbackStore!.deleteFeedback(
        organizationId,
        feedbackToDelete.id,
      );
      setFeedbackToDelete(undefined);
      if (success) {
        fetchFeedback();
      }
    };

    return (
      <>
        <FeedbackListUI
          feedbackList={feedbackList}
          isLoading={feedbackStore!.isLoading}
          canEdit={canEdit}
          search={search}
          setSearch={setSearch}
          onCreate={() => navigate(`${listRoute}/create`)}
          onOpen={feedback => navigate(`${listRoute}/${feedback.id}`)}
          onDelete={setFeedbackToDelete}
          loadNextPage={loadNextPage}
        />
        {deleteFade.rendered && (
          <ModalFade closing={deleteFade.closing}>
            <ConfirmationModal
              shown
              title={feedbackText.deleteConfirm}
              acceptText={feedbackText.delete}
              onAccept={onConfirmDelete}
              onCancel={() => setFeedbackToDelete(undefined)}
            />
          </ModalFade>
        )}
      </>
    );
  }),
);

export default FeedbackListContainer;
